import { useState, useCallback } from "react";
import type { LearnerGroup, SmartGroupCriteria, AvailableLearner } from "../types/groupTypes";

const MAX_GROUPS = 3;

interface UseLearnerGroupsReturn {
  groups: LearnerGroup[];
  createGroup: (name: string, memberIds: (number | string)[]) => boolean;
  deleteGroup: (groupId: string) => void;
  createSmartGroup: (
    name: string,
    criteria: SmartGroupCriteria,
    allLearners: AvailableLearner[],
  ) => boolean;
  canCreateGroup: boolean;
}

export const useLearnerGroups = (): UseLearnerGroupsReturn => {
  const [groups, setGroups] = useState<LearnerGroup[]>([]);

  const canCreateGroup = groups.length < MAX_GROUPS;

  const createGroup = useCallback(
    (name: string, memberIds: (number | string)[]): boolean => {
      if (groups.length >= MAX_GROUPS) return false;
      if (memberIds.length === 0) return false;

      const newGroup: LearnerGroup = {
        id: crypto.randomUUID(),
        name,
        memberIds,
        createdAt: new Date(),
      };
      setGroups((prev) => [...prev, newGroup]);
      return true;
    },
    [groups.length],
  );

  const deleteGroup = useCallback((groupId: string) => {
    setGroups((prev) => prev.filter((g) => g.id !== groupId));
  }, []);

  const createSmartGroup = useCallback(
    (name: string, criteria: SmartGroupCriteria, allLearners: AvailableLearner[]): boolean => {
      if (groups.length >= MAX_GROUPS) return false;
      if (allLearners.length === 0) return false;

      let filteredLearners: AvailableLearner[] = [];

      switch (criteria.type) {
        case "top_performers": {
          // Sort by average score (descending), then take top X%
          const sortedByScore = [...allLearners]
            .filter((l) => l.averageScore !== null)
            .sort((a, b) => (b.averageScore ?? 0) - (a.averageScore ?? 0));

          const topCount = Math.ceil(sortedByScore.length * (criteria.percentage / 100));
          filteredLearners = sortedByScore.slice(0, topCount);
          break;
        }

        case "struggling": {
          // Learners with completion rate below threshold
          filteredLearners = allLearners.filter(
            (l) => l.completionRate < criteria.completionThreshold,
          );
          break;
        }

        case "active_this_month": {
          // Learners with a session in the current month
          const now = new Date();
          const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

          filteredLearners = allLearners.filter((l) => {
            if (!l.lastSessionDate) return false;
            return l.lastSessionDate >= startOfMonth;
          });
          break;
        }

        case "by_parcours": {
          // This requires session data filtering by parcours
          // For now, this is handled in the modal component where we have access to session data
          // The modal will pre-filter learners before calling createSmartGroup
          filteredLearners = allLearners;
          break;
        }
      }

      if (filteredLearners.length === 0) return false;

      const memberIds = filteredLearners.map((l) => l.id);
      return createGroup(name, memberIds);
    },
    [groups.length, createGroup],
  );

  return {
    groups,
    createGroup,
    deleteGroup,
    createSmartGroup,
    canCreateGroup,
  };
};
